import type { AttributeEffect, AttributeValue, Garment } from '@fitting-room/contracts'
import { attributeBag } from '../lib/attributes'
import { bestImage } from '../lib/media'

/**
 * The thing in the catalogue that shows what an option means.
 *
 * An option already names the attribute values it favours, and the catalogue
 * is full of things carrying them — so the picture for a word is the first
 * item that IS that word. Nothing new is authored and nothing is hard-coded:
 * as the boutique tags its stock, the screen fills in on its own.
 *
 * Pure and deterministic, which is the whole point: a screen of photographs
 * that reshuffles itself between two taps reads as broken, and what a word
 * looks like must not be decided by how well it happens to be ranking this
 * minute.
 *
 * So the candidates are read in ID order rather than in the order they arrive.
 * The only page deep enough to ask is the ranked one, and its order moves with
 * every answer — taking the first match off it would mean the picture for a
 * word changed when she went back and edited a screen before it. Lowest id is
 * arbitrary, and being arbitrary is the property that is wanted: the same word
 * gets the same picture for the whole session.
 *
 * One thing this cannot fix from here: the page is 200 of a catalogue that can
 * hold more, and which 200 is still a ranking decision. A boutique large enough
 * for that to bite needs an endpoint that answers "what does the catalogue
 * hold" without ranking it first.
 */
export function exemplarFor(
  effects: readonly AttributeEffect[],
  garments: readonly Garment[],
  /** Image sources already spoken for on this screen. See `exemplarsFor`. */
  taken?: ReadonlySet<string>,
): Garment | undefined {
  /** attributeKey -> the values this option is FOR. */
  const wanted = new Map<string, Set<string>>()
  for (const effect of effects) {
    // A negative weight is "not this". Illustrating an option with the very
    // thing it pushes away would say the opposite of what it means.
    if (effect.weight <= 0) continue
    let values = wanted.get(effect.attributeKey)
    if (!values) {
      values = new Set<string>()
      wanted.set(effect.attributeKey, values)
    }
    for (const value of effect.prefer) values.add(value)
  }
  // An effect-free option — "no preference" — is about the question, not about
  // anything on a rail, and there is nothing that could illustrate it.
  if (wanted.size === 0) return undefined

  for (const garment of [...garments].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))) {
    if (taken) {
      const image = bestImage(garment.media)
      if (image && taken.has(image.src)) continue
    }
    const bag = attributeBag(garment)
    for (const [key, values] of wanted) {
      if (carries(bag.get(key), values)) return garment
    }
  }
  return undefined
}

/**
 * Whether one assignment counts as one of the wanted values.
 *
 * A multi-select attribute arrives as an array and a single-select as a
 * string, and both are ordinary here: the caller should not have to know which
 * kind of attribute an effect happens to name.
 */
function carries(value: AttributeValue | undefined, wanted: ReadonlySet<string>): boolean {
  if (typeof value === 'string') return wanted.has(value)
  if (Array.isArray(value)) return value.some((v) => wanted.has(v))
  // A number or a boolean is a measurement or a flag. `prefer` holds option
  // values from the attribute's own list, so neither can ever be one — and
  // stringifying them to compare would match "8" against a value named 8.
  return false
}

/**
 * The picture for every option on one screen, chosen so they are not all the
 * same picture.
 *
 * `exemplarFor` answers one option at a time and has no way to know what the
 * option beside it drew. That is fine against a catalogue where every style
 * has its own photographs and merely unfortunate against one where they are
 * shared — the demo's 28 gowns carry 6 images between them, so four necklines
 * illustrated by four genuinely different gowns came out as two photographs
 * printed twice, which reads as broken even though nothing is.
 *
 * So an option prefers a match nothing above it has already used, and falls
 * back to the first match when the catalogue has nothing else to offer. The
 * order of the screen decides who gets first pick, which is stable for the
 * same reason the choice within an option is: neither may depend on ranking.
 */
export function exemplarsFor(
  options: readonly {
    readonly id: string
    readonly effects: readonly AttributeEffect[]
    /** The boutique's own choice of picture. See `choiceOptionSchema`. */
    readonly exemplarGarmentId?: string | undefined
  }[],
  garments: readonly Garment[],
): Map<string, Garment> {
  const chosen = new Map<string, Garment>()
  const taken = new Set<string>()
  for (const option of options) {
    /*
     * A named picture wins outright — over the rule, and over the
     * not-twice-on-one-screen preference below it. Both of those exist to
     * answer "which of these would she recognise the word by", which is the
     * question the boutique has just answered for itself; a pin quietly
     * overruled by the option above it would be a setting that works until
     * two options are pinned to the same gown.
     *
     * An id nothing on the page carries falls through to the rule rather than
     * leaving a hole: the page is 200 deep and the pinned gown may simply not
     * be on it today.
     */
    const pinned = option.exemplarGarmentId
      ? garments.find((garment) => garment.id === option.exemplarGarmentId)
      : undefined
    const fresh = pinned ?? exemplarFor(option.effects, garments, taken)
    const match = fresh ?? exemplarFor(option.effects, garments)
    if (!match) continue
    chosen.set(option.id, match)
    const image = bestImage(match.media)
    if (image) taken.add(image.src)
  }
  return chosen
}
